import React from "react";
import { Box } from "@mui/material";
import { useFormContext } from "react-hook-form";

import {
  canadianProvincesOptions,
  countryOptions,
  stateAbbrOptions,
} from "../data";
import {
  SelectRHF,
  TextFieldRHF,
  AutocompleteRHF,
} from "./RHFWrappedComponents";

interface LocationProps {
  /**
   * The name of the parent RHF data key under which location information will be stored.
   */
  parentName: string;

  countryList?: { label: string; value: string }[];

  /**
   * If true the address input section will be shown.
   */
  showAddress?: boolean;

  /**
   * Indicates whether the county, zip code, or both options should be shown.
   */
  countyOrZip?: "county" | "zip" | "both";

  stateField?: string;

  stateIsRequired?: boolean;
}

export function State({
  name,
  countryField,
  stateIsRequired,
}: {
  name: string;
  countryField: string;
  stateIsRequired: boolean;
}) {
  const { watch } = useFormContext();
  const country = watch(countryField);
  const stateSelectSx = {
    minWidth: "5.813rem",
  };
  if (country === "US") {
    return (
      <SelectRHF
        name={name}
        selectLabel="State"
        menuItemOptions={stateAbbrOptions}
        sx={stateSelectSx}
        rules={stateIsRequired ? { required: "State is required" } : {}}
      />
    );
  }

  if (country === "CA") {
    return (
      <SelectRHF
        name={name}
        selectLabel="State"
        menuItemOptions={canadianProvincesOptions}
        sx={stateSelectSx}
        rules={stateIsRequired ? { required: "State is required" } : {}}
      />
    );
  }

  return (
    <TextFieldRHF
      name={name}
      textFieldLabel={"State/Province"}
      sx={{
        flex: 1,
      }}
      rules={stateIsRequired ? { required: "State is required" } : {}}
    />
  );
}

export function Location({
  parentName,
  countryList,
  showAddress = true,
  countyOrZip = "county",
  stateField = "state",
  stateIsRequired = false,
}: LocationProps) {
  const countryOptionsList = countryList ? countryList : countryOptions;
  return (
    <>
      {showAddress && (
        <>
          <TextFieldRHF
            name={`${parentName}.addressLine1`}
            textFieldLabel="Address Line 1"
          />

          <TextFieldRHF
            name={`${parentName}.addressLine2`}
            textFieldLabel="Address Line 2"
          />
        </>
      )}
      <AutocompleteRHF
        name={`${parentName}.country`}
        selectLabel="Country"
        options={countryOptionsList.map(({ value }) => value)}
        getOptionLabel={(option) => {
          const countryLabel = countryOptionsList.find(
            (country) => country.value === option
          )?.label;
          return countryLabel || "";
        }}
      />

      <TextFieldRHF name={`${parentName}.city`} textFieldLabel="City" />
      <Box display="flex">
        <>
          {(countyOrZip === "county" ||
            countyOrZip === "both" ||
            countyOrZip === undefined) && (
            <TextFieldRHF
              name={`${parentName}.county`}
              textFieldLabel={"County"}
              sx={{
                flex: 1,
                mr: 1,
              }}
            />
          )}
          {(countyOrZip === "zip" || countyOrZip === "both") && (
            <TextFieldRHF
              name={`${parentName}.zipOrPostalCode`}
              textFieldLabel={"Zip Code"}
              sx={{
                flex: 1,
                mr: 1,
              }}
            />
          )}
          <State
            name={`${parentName}.${stateField}`}
            countryField={`${parentName}.country`}
            stateIsRequired={stateIsRequired}
          />
        </>
      </Box>
    </>
  );
}
